import React from 'react';
import { HugeiconsIcon } from '@hugeicons/react';
import {
  ApiIcon,
  BrowserIcon,
  FileUploadIcon,
  HelpCircleIcon,
  Message01Icon,
  UserIcon
} from '@hugeicons/core-free-icons';
import { LeadUiModel } from '../../shared/ui-models/lead.ui-model';
import { attributionTooltip, sourceChipText, sourceLabel } from './lead-source.util';

/**
 * A colour-coded glyph per mechanism.
 *
 * Three attempts, so the reasoning is worth recording:
 *
 *   1. Bare grey line icons. All five read as the same smudge in a scanned
 *      column — the shape only resolved once you were close enough to have
 *      hovered anyway.
 *   2. Letter avatars (WF, AD, …). Legible, but letters are not pictures and
 *      a column of two-letter codes is something to decode, not glance at.
 *   3. Glyphs on filled 20px discs. A 12px icon inside a 20px circle leaves
 *      almost no padding, so the strokes crowded the edge and every disc
 *      read as a blob rather than a picture.
 *
 * What works is the plainest version of the idea: the same glyphs, bigger,
 * in their own colour, with nothing behind them. Colour separates the sources
 * across the column and the glyph is legible because it has room.
 *
 * Every colour comes from `tailwind.config.js`, not Tailwind's stock palette.
 * A guard in `lead-visuals.test.ts` enforces that, and its comment explains
 * why: stock colours are ones no design change reaches and no other screen
 * reuses, which is how these screens previously drifted into looking like a
 * different product.
 */
const SOURCE_AVATAR: Record<string, { icon: typeof BrowserIcon; color: string }> = {
  web_form: { icon: BrowserIcon, color: 'text-primary-500' },
  admin: { icon: UserIcon, color: 'text-neutral-500' },
  api: { icon: ApiIcon, color: 'text-action-primary' },
  file_upload: { icon: FileUploadIcon, color: 'text-secondary-500' },
  messaging: { icon: Message01Icon, color: 'text-secondary-700' }
};

/** Anything the service adds later still gets a glyph, never a blank cell. */
const UNKNOWN_AVATAR = { icon: HelpCircleIcon, color: 'text-neutral-300' };

/**
 * Where a lead came from: an avatar at rest, the words on hover.
 *
 * **The expansion happens inside a fixed-width column.** It used to widen the
 * cell, which pushed every column to its right and made the whole table jump
 * as the pointer crossed the queue. The column is now sized for the widest
 * expanded label, so hovering changes what is drawn and never what is laid
 * out. An absolutely-positioned overlay would also have worked, except the
 * table lives in an `overflow-x-auto` wrapper that would clip it.
 *
 * `max-w-0` on the label is deliberate — collapsed until `group-hover` opens
 * it. The same class blanked the Last comment column once by accident, so it
 * is worth saying which one this is.
 *
 * The label stays in the DOM rather than being swapped in on hover, so screen
 * readers and in-page search still find it; `title` carries the full
 * attribution underneath.
 */
export const LeadSourceChip: React.FC<{ lead: LeadUiModel }> = ({ lead }) => {
  const avatar = SOURCE_AVATAR[lead.source] ?? UNKNOWN_AVATAR;

  return (
    <span
      className="group inline-flex max-w-full items-center rounded-full px-1 py-0.5 text-[11px] font-gilroy-medium text-neutral-500 transition-colors hover:bg-neutral-50"
      title={attributionTooltip(lead)}
      aria-label={`Source: ${sourceLabel(lead.source)}`}
    >
      <span className={`flex shrink-0 items-center ${avatar.color}`} aria-hidden="true">
        {/*
          16px with a light stroke. The previous 12px glyph carried a heavier
          stroke to survive being shrunk, which is what made it look clotted.
        */}
        <HugeiconsIcon icon={avatar.icon} size={16} strokeWidth={1.5} />
      </span>
      <span className="max-w-0 overflow-hidden whitespace-nowrap opacity-0 transition-all duration-200 ease-out group-hover:ml-1.5 group-hover:max-w-[9rem] group-hover:opacity-100 group-hover:pr-1">
        {sourceChipText(lead)}
      </span>
    </span>
  );
};
